import { useState, useEffect, useMemo, useRef } from 'react';
import { Button, Dropdown, Nav } from 'react-bootstrap';
import { toast } from 'sonner';
import { UserForm } from './UserForm';
import { TeamForm } from './TeamForm';
import { TableComponent } from '../TableComponent';
import { useNavigate } from "react-router-dom";
import useLocalStorageState from 'use-local-storage-state';

//used for Pagination
import RenderPagination from './RenderPagination';
import { formatDate, PaginationButtons } from '../../Utils/helpers/commonhelper'

//Normal API requests
import {
  GetRequest,
  PutRequest,
  DeleteRequest,
  PostRequest,
} from '../../Utils/hooks/ApiHandler';

//Tanstack query requests
import {
  useGetRequest,
  usePostRequest
} from '../../Utils/hooks/QueryHandler';
import { AuthorizationCard } from '../../pages/AuthorizationCard';
import { baseURL } from '../../Utils/constants/constant';
import DeleteUserModal from '../DeleteUserModal';
import DeleteModal from '../DeleteModal';


// Main user Component
export const Users = () => {
  // Session storage and authentication
  const [deleteUserData, setDeleteUserData] = useState(null);
  const [deleteUserLoading, setDeleteUserLoading] = useState(false);

  const [customer, setCustomer] = useLocalStorageState("customer");
  const navigate = useNavigate();
  const customerId = customer?.customer_details?.customer_id;
  const loginUserId = customer?.customer_details?.id;
  const token = customer?.token?.authorization;
  const rf = customer?.token?.authentication;

  // Request headers
  const headers = {
    Authentication: rf,
    Authorization: token
  }

  // Common props for API calls
  const extras = { customer, setCustomer, rf, navigate };

  // States
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [roles, setRoles] = useState([]);
  const [showUserForm, setShowUserForm] = useState(false);
  const [showTeamForm, setShowTeamForm] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [editUserData, setEditUserData] = useState(null);
  const [editTeamId, setEditTeamId] = useState(null);
  const [editTeamData, setEditTeamData] = useState(null);
  const [activeTab, setActiveTab] = useState('users');
  const [addTeam, setAddTeam] = useState(false);
  const [alreadySelectedTeam, setAlreadySelectedTeam] = useState([]);
  const [loading, setLoading] = useState({
    users: false,
    teams: false,
    roles: false,
    formSubmit: false,
    userForm: false,
    teamForm: false
  });
  const timeoutRef = useRef(null);
  useEffect(() => {
    if (!activeTab) return;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setLoading((prev) => ({
      ...prev,
      users: false,
      teams: false,
      roles: false,
      [activeTab]: true,
    }));

    timeoutRef.current = setTimeout(() => {
      setLoading((prev) => ({
        ...prev,
        [activeTab]: false,
      }));
    }, 2000);

    // cleanup
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [activeTab]);

  const [membersId, setMembersId] = useState([]);
  const [pipelineId, setPipelineId] = useState([]);
  // const[visibility, setVisibility] = useState("");
  const [isDeleting, setIsDeleting] = useState(false)



  //Delete modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteType, setDeleteType] = useState(null);
  const [deleteLoading, setdeleteLoading] = useState(false)

  // States for using pagination
  const [limit, setLimit] = useState(25);
  const limitoptions = [25, 50, 100, 250, 500];
  const [userOffset, setUserOffset] = useState(0);
  const [teamOffset, setTeamOffset] = useState(0);
  const [userPageButtons, setUserPageButtons] = useState([]);
  const [teamPageButtons, setTeamPageButtons] = useState([]);
  const [openrow, setOpenRow] = useState([]);

  // Separate search states for users and teams
  const [userSearchInput, setUserSearchInput] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [teamSearchInput, setTeamSearchInput] = useState('');
  const [teamSearch, setTeamSearch] = useState('');

  // permission for user management
  const permission = customer.customer_details?.permission
  const settingPermision = permission?.filter(c => c.module_name === "setting")
  const user_type = Number(customer.customer_details?.user_type)
  const userGrant = {
    isView: user_type !== 1 ? Boolean(Number(settingPermision?.find(c => c.feature_name?.toLowerCase()?.split(" ")?.join("_") === "user_management")?.view)) : true,
    isCreate: user_type !== 1 ? Boolean(Number(settingPermision?.find(c => c.feature_name?.toLowerCase()?.split(" ")?.join("_") === "user_management")?.create)) : true,
    isEdit: user_type !== 1 ? Boolean(Number(settingPermision?.find(c => c.feature_name?.toLowerCase()?.split(" ")?.join("_") === "user_management")?.edit)) : true,
    isDelete: user_type !== 1 ? Boolean(Number(settingPermision?.find(c => c.feature_name?.toLowerCase()?.split(" ")?.join("_") === "user_management")?.delete)) : true
  }
  // Separate params for users and teams
  //useMemo is used for unnecessory rerenders. otherwise will create a new data object everytime if rerender. now only create object when params changing.
  const userParams = useMemo(() => ({
    limit,
    offset: userOffset,
    search: userSearch
  }), [limit, userOffset, userSearch]);

  const teamParams = useMemo(() => ({
    limit,
    offset: teamOffset,
    search: teamSearch
  }), [limit, teamOffset, teamSearch]);

  // Data fetching hooks
  const {
    data: usersData,
    isLoading: isUsersLoading,
    refetch: refetchUsers
  } = useGetRequest(`users/users/fetchAllUser/${customerId}`, ['fetchAllUsers', userParams], headers, extras, userParams);

  const {
    data: teamsData,
    isLoading: isTeamsLoading,
    refetch: refetchTeams
  } = useGetRequest(`users/users/fetchTeams/${customerId}`, ['fetchAllTeams', teamParams], headers, extras, teamParams);

  // Fetch all teams separately for user form
  const fetchAllTeams = async () => {
    try {
      const response = await GetRequest(`users/users/fetchAllTeams/${customerId}`, { headers }, extras);
      if (response?.status === 'success') {
        setAllTeams(response.data || []);
      }
    } catch (error) {
      setAllTeams([]);
    }
  };

  // Handle users data when it changes
  useEffect(() => {
    if (usersData?.status === 'success') {
      setUsers(usersData.data || []);
      setUserPageButtons(PaginationButtons(usersData.total_count, limit))
    } else if (usersData?.status === 'fail') {
      setUsers([]);
    }
  }, [usersData, limit]);

  // Effect to handle teams data when it changes
  useEffect(() => {
    if (teamsData?.status === 'success') {
      setTeams(teamsData.data || []);
      setTeamPageButtons(PaginationButtons(teamsData.total_count, limit))
    } else if (teamsData?.status === 'fail') {
      setTeams([]);
    }
  }, [teamsData, limit]);

  // Fetches roles from the API to render in the userform 
  const fetchRoles = async () => {
    setLoading(prev => ({ ...prev, roles: true }));
    try {
      const response = await GetRequest(`users/Roles/getRoles/${customerId}/`, { headers }, extras);
      if (response?.status === 'success') {
        setRoles(response.data || []);
      } else {
        toast.error(response?.message || 'Failed to load roles');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to load roles');
      setRoles([]);
    } finally {
      setLoading(prev => ({ ...prev, roles: false }));
    }
  };

  // Live search for users
  useEffect(() => {
    const timer = setTimeout(() => {
      setUserSearch(userSearchInput);
      setUserOffset(0);
    }, 400);

    return () => clearTimeout(timer);
  }, [userSearchInput]);

  // Live search for teams
  useEffect(() => {
    const timer = setTimeout(() => {
      setTeamSearch(teamSearchInput);
      setTeamOffset(0);
    }, 400);

    return () => clearTimeout(timer);
  }, [teamSearchInput]);

  const clearTeamSearch = () => {
    setTeamSearchInput('');
    setTeamSearch('');
    setTeamOffset(0);
  };

  // Handles opening the user form in add mode
  const handleAddUser = () => {
    setEditUserId(null);
    setEditUserData(null);
    setShowUserForm(true);
    fetchRoles();
    fetchAllTeams();
  };

  // Handles opening the team form in add mode
  const handleAddTeam = () => {
    setEditTeamId(null);
    setEditTeamData(null);
    setShowTeamForm(true)
  };

  // Handles opening the user form in edit mode
  const handleEditUser = async (id) => {
    fetchRoles();
    fetchAllTeams();
    setLoading(prev => ({ ...prev, userForm: true }));
    try {
      const response = await GetRequest(`users/users/getUserById/${id}/`, { headers }, extras);
      if (response?.status === 'success') {
        const user = response.data;
        const teams = Array.isArray(user.teams_id) ? user.teams_id.map((t) => t.team_id.toString()) : [];

        const mappedUser = {
          user_id: user[0].id,
          firstName: user[0].first_name,
          lastName: user[0].last_name,
          email: user[0].email,
          salutation: user[0].salutation || 'Mr',
          role: user[0].role_id?.toString() || '',
          teams,
          phone: user[0].phone || '',
          countryCode: user[0].country_code || '',
          phoneType: user[0].phone_type || '',
          assignTo: user[0].assign_to || '',
          department:user[0]?.department_id?.toString() || "",
          designation:user[0]?.designation_id?.toString()|"",
          status : user[0]?.status
        };
        setEditUserId(id);
        setEditUserData(mappedUser);
        setShowUserForm(true);
      } else {
        toast.error(response?.message || 'Failed to load user data');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to load user data');
    } finally {
      setLoading(prev => ({ ...prev, userForm: false }));
    }
  };

  // Handles opening the team form in edit mode
  const handleEditTeam = async (id) => {
    setLoading(prev => ({ ...prev, teamForm: true }));
    try {
      const response = await GetRequest(`users/users/getTeamDetails/${id}`, { headers }, extras);

      if (response?.status === 'success') {
        setEditTeamId(id);
        setEditTeamData(response.data);
        const pipelineRes = response.data[0].pipeline.map((p) => p.pipeline_id)

        setMembersId(response.data[0].members)
        setShowTeamForm(true);
        setPipelineId(pipelineRes);

      } else {
        toast.error(response?.message || 'Failed to load team data');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to load team data');
    } finally {
      setLoading(prev => ({ ...prev, teamForm: false }));
    }
  };

  const handleDeleteClick = async (id, type) => {
    setDeleteId(id);
    setDeleteType(type);


    if (type === "user") {
      setDeleteUserLoading(true);
      try {
        const response = await GetRequest(
          `users/users/getUserDetails/${id}/`,
          { headers },
          extras
        );

        if (response?.status === "success") {
          const user = response.data[0];

          setDeleteUserData({
            name: `${user.user_fill_name}`,
            email: user.email,
            userSince: user.created_at
              ? formatDate(user.created_at)
              : "-",
            addedBy: user.added_by || "Default",
          });

          setShowDeleteModal(true);
        } else {
          toast.error("Failed to fetch user details");
        }
      } catch (error) {
        toast.error(error.message || "Failed to fetch user details");
      } finally {
        setDeleteUserLoading(false);
      }
    }

    if (type === "team") {
      setShowDeleteModal(true);
    }
  };


  const handleDeleteClose = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
    setDeleteType(null);
    setDeleteUserData(null);
  };

  // Handles confirming the delete action
  const handleDeleteConfirm = async () => {

    setIsDeleting(true)
    try {
      let response;
      if (deleteType === 'user') {
        response = await DeleteRequest(`users/users/deleteUser/${deleteId}/`, { headers }, extras);
      } else {
        response = await DeleteRequest(`users/users/deleteTeam/${deleteId}`, { headers }, extras);
      }
      if (response?.status === 'success') {
        toast.success(response.message || "Deleted successfully");
        deleteType === 'user' ? await refetchUsers() : await refetchTeams();
        setIsDeleting(false)
        handleDeleteClose();
      } else {
        toast.error(response?.message || "Failed to delete");
        setIsDeleting(false)
      }
    } catch (error) {
      toast.error(error.message || "An error occurred");
    } finally {
      setIsDeleting(false)
    }
  };

  // Mutation hooks for form submissions
  const { mutate: addUser } = usePostRequest('users/users/addUser', false, { headers }, { customer, setCustomer, rf, navigate });
  const { mutate: createTeam } = usePostRequest('users/users/createTeam/', false, { headers }, { customer, setCustomer, rf, navigate });

  // Handles user form submission (both add and edit)
  const handleUserSubmit = async (data) => {
    if (!userGrant?.isCreate) {
      toast.warning("You are not authorized for this action!")
      return
    }
    setLoading(prev => ({ ...prev, formSubmit: true, userForm: true }));

    //checks the array and modify string to numbers
    const teamIds = Array.isArray(data.team) ? data.team.map(Number) : [Number(data.team)];

    const payload = {
      login_user_id: Number(loginUserId),
      customer_id: Number(customerId),
      role_id: Number(data.role),
      team_id: teamIds,
      first_name: data.firstName,
      last_name: data.lastName,
      email: data.email,
      salutation: data.salutation,
      country_code: data.dialCode || data.countryCode,
      phone_type: data.phoneType,
      phone: data.phone,
      assign_to: data.leadOwnerId,
      department:Number(data?.department),
      designation:Number(data?.designation),
      user_phone: [
        {
          country_code: data.dialCode || data.countryCode,
          type: data.phoneType,
          phone: data.phone,
        },
      ],
      status : data.status
    };

    try {
      //If user has id then send update requests
      if (data.user_id) {
        const response = await PutRequest('users/users/updateUser', { ...payload, user_id: Number(data.user_id) }, { headers }, extras);
        if (response?.status === 'success') {
          toast.success(response.message || 'User updated successfully');
          setShowUserForm(false);
          await refetchUsers();
        } else {
          toast.error(response?.message || 'Failed to update user');
        }
      } else { //otherwise send adding request
        addUser(payload, {
          onSuccess: (response) => {
            if (response.status === "fail" || response.success === false) {
              toast.error(response?.message || 'Failed to update user');
              return;
            }

            setShowUserForm(false);
            refetchUsers();
            setActiveTab('users');
            toast.success(response.message);
          },
          onError: (error) => {
            toast.error(error?.message || 'Failed to add user');
          },
          onSettled: () => {
            setLoading(prev => ({ ...prev, formSubmit: false, userForm: false }));
          }
        });
        return;
      }
    } catch (error) {
      toast.error(error.message || 'Failed to save user');
    } finally {
      setAlreadySelectedTeam([]);
      if (data.user_id) {
        setLoading(prev => ({ ...prev, formSubmit: false, userForm: false }));
      }
    }
  };

  // Handles team form submission (both add and edit)
  const handleTeamSubmit = async (data) => {
    if (!userGrant?.isCreate) {
      toast.warning("You are not authorized for this action!")
      return
    }
    setLoading(prev => ({ ...prev, formSubmit: true, teamForm: true }));

    const userpayload = {
      members_id: data.members_id,
      pipeline_id: data.pipeline_id,
      visibility: data.visibility,
      team_name: data.team_name,
      description: data.description,
      status: data.status,
      customer_id: customerId,
      user_login_id: loginUserId
    };


    try {
      let response;

      if (data.id) {
        // For update operation
        response = await PutRequest(
          'users/users/updateTeam/',
          { ...userpayload, team_id: Number(data.id) },
          { headers },
          extras
        );
      } else {
        // For create operation
        response = await new Promise((resolve) => {
          createTeam(userpayload, {
            onSuccess: (res) => resolve(res),
            onError: (error) => {
              toast.error(error?.message || 'Failed to add team');
              resolve(null);
            }
          });
        });
      }

      if (response?.status === 'success') {
        toast.success(response.message || `Team ${data.id ? 'updated' : 'added'} successfully`);
        // Refetch teams data
        await refetchTeams();
        await fetchAllTeams();
        if (addTeam) {
          setShowTeamForm(false);
          setShowUserForm(true);
          return {
            ...response,
            data: response.data
          };
        } else {
          setActiveTab('teams');
        }
      } else {
        toast.error(response?.message || `Failed to ${data.id ? 'update' : 'add'} team`);
      }
      return response;
    } catch (error) {
      toast.error(error?.message || 'Failed to save team');
      return null;
    } finally {
      setLoading(prev => ({ ...prev, formSubmit: false, teamForm: false }));
    }
  };

  // Formats a user's full name with salutation
  const formatUserName = (row) => {
    return `${row.salutation ? row.salutation + ' ' : ''}${row.full_name}`;
  };

  // Formats a phone number with country code
  const formatPhoneNumber = (row) => {
    return row.country_code ? `+${row.country_code} ${row.phone}` : row.phone;
  };

  // Renders a status badge with appropriate color
  const renderStatusBadge = (status) => {
    let ClassName;
    if (status === 'Active') ClassName = 'btn btn-sm btn-primary-light';
    else if (status === 'Inactive') ClassName = 'btn btn-sm btn-danger-light';
    else if (status === 'unverified') ClassName = 'btn btn-sm btn-warning-light';

    return (
      <button className={ClassName}>
        {status}
      </button>
    );
  };
  const tabs = [
    {
      key: "users",
      label: "Users",
      icon: <i className="bi bi-people"></i>,
    },
    {
      key: "teams",
      label: "Teams",
      icon: <i className="bi bi-diagram-3"></i>,
    },
  ];

  const handleSendMail = (user_id) => {
    const payload = {
      user_id: user_id,
      customer_id: customerId
    }

    const res = PostRequest(`${baseURL}users/users/sendMail`, payload, false, { headers }, extras)
    if (res.status === "fail") {
      return toast.error(res?.message || "error in sending mail");
    }
    toast.success("Mail sent successfully!");
  };

  return (
    <div className="container">
      <div className="d-flex justify-content-between align-items-center text-start">

        <div style={{ lineHeight: "0.8rem" }}>
          <h6
            // className="addEnquiryHead"
            style={{ fontWeight: "500", color: "black", fontSize: "1.0rem" }}
          >
            Users & Teams
          </h6>
          <p className="text-muted mb-0 small" style={{ color: "#c7c7c7" }}>
            You can manage users and teams from here.
          </p>
        </div>
        <div className='d-flex'>
          <Dropdown className="me-3">
            <Dropdown.Toggle className=" btn btn-default no-arrow" style={{ display: "flex", alignItems: "center", paddingRight: "7px" }}>
              {limit}
              <i class="bx bx-chevron-down" style={{ fontSize: "21px" }}></i>
            </Dropdown.Toggle>
            <Dropdown.Menu align="end" style={{ minWidth: '2rem', textAlign: 'center' }}>
              {limitoptions.map((el) => (
                <Dropdown.Item key={el} onClick={() => {
                  setLimit(el);
                  setTeamOffset(0);
                  setUserOffset(0);
                }}>{el}</Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>

          <Dropdown>
            <Dropdown.Toggle className=" btn btn-default no-arrow" style={{ display: "flex", alignItems: "center", paddingRight: "7px" }}>
              Action
              {/* <i className="bi bi-caret-down-fill d-inline-block ps-1" ></i> */}
              <i class="bx bx-chevron-down" style={{ fontSize: "21px" }}></i>
            </Dropdown.Toggle>
            <Dropdown.Menu align="end">
              <Dropdown.Item onClick={() => {
                if (!userGrant?.isCreate) {
                  toast.warning("You are not authorized for this action!")
                  return
                }
                handleAddUser()
              }}>Add User</Dropdown.Item>
              <Dropdown.Item onClick={() => {
                if (!userGrant?.isCreate) {
                  toast.warning("You are not authorized for this action!")
                  return
                }
                handleAddTeam()
              }}>Add Team</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
      <hr
        className="mb-4"
        style={{ borderTop: "0.5px solid #6c757d", opacity: "0.3", marginTop: "0.7rem" }}
      />

      <div className="d-flex justify-content-between align-items-center mb-3">
        {/* Tabs */}
        <div className="d-flex gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              aria-selected={activeTab === tab.key}
              className={`d-flex align-items-center gap-1 rounded px-3 py-2 ${activeTab === tab.key
                ? "fw-semibold text-white bg-primary border-0"
                : "text-dark bg-light border"
                }`}
              style={
                activeTab === tab.key
                  ? {}
                  : { border: "1px solid #dedddd" }
              }
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div style={{ width: "250px" }}>
          {activeTab === "users" && (
            <div className=" position-relative mt-1 mt-md-0">
              <input
                disabled={!userGrant?.isView}
                type="search"
                className="form-control "
                placeholder="Search..."
                value={userSearchInput}
                onChange={(e) => setUserSearchInput(e.target.value)}
                style={{
                  fontSize: "12px",
                  paddingLeft: "36px",
                  height: "35px",
                }}
              />
              <i
                class="fa-solid fa-magnifying-glass"
                style={{
                  position: "absolute",
                  marginTop: "-26px",
                  zIndex: 1,
                  marginLeft: "13px",
                }}
              ></i>
            </div>
          )}

          {activeTab === "teams" && (
            <div className=" position-relative mt-1 mb-2 mt-md-0">
              <input
                disabled={!userGrant?.isView}
                type="search"
                className="form-control "
                placeholder="Search..."
                value={teamSearchInput}
                onChange={(e) => setTeamSearchInput(e.target.value)}
                style={{
                  fontSize: "12px",
                  paddingLeft: "36px",
                  height: "35px",
                }}
              />
              <i
                class="fa-solid fa-magnifying-glass"
                style={{
                  position: "absolute",
                  marginTop: "-26px",
                  zIndex: 1,
                  marginLeft: "13px",
                }}
              ></i>
            </div>
          )}
        </div>
      </div>

      {/* USERS conditional render */}
      {activeTab === "users" && (
        <>
          {userGrant?.isView ? (
            <>
              <TableComponent
                data={users}
                loading={loading?.users || loading?.teams || false}
                columns={["full_name", "email", "phone", "role_title", "status"]}
                columnLabels={{
                  full_name: "Name",
                  role_title: "Role",
                  phone: "Phone",

                }}
                customRenderers={{
                  status: (row) => renderStatusBadge(row.status),
                  full_name: (row) => formatUserName(row),
                  phone: (row) => formatPhoneNumber(row),
                }}
                actions={(row) => (
                  <>
                    {row.user_type !== "1" && (
                      <>
                        <div className="endTdDiv">
                          <Dropdown
                            as="div"
                            className="dropdownsWithoutToggle"
                            onToggle={(isOpen) => setOpenRow(isOpen ? row.id : null)}
                          >
                            <Dropdown.Toggle as={Nav.Link} className="header-link">
                              <i className="bi bi-three-dots-vertical" />
                            </Dropdown.Toggle>

                            <Dropdown.Menu
                              align="end"
                              style={{
                                minWidth: "9rem",
                                boxShadow: "0px 0px 15px rgb(215,215,215)",
                                marginRight: "40px"
                              }}
                            >
                              {/* EDIT */}
                              <Dropdown.Item
                                className="d-flex align-items-center gap-2"
                                disabled={loading.userForm}
                                onClick={() => {
                                  if (!userGrant?.isEdit) {
                                    toast.warning("You are not authorized for this action");
                                    return;
                                  }
                                  row.id && handleEditUser(row.id);
                                }}
                              >
                                {loading.userForm && editUserId === row.id ? (
                                  <span className="spinner-border spinner-border-sm" />
                                ) : (
                                  <i className="bi bi-pencil" />
                                )}
                                <span>Edit</span>
                              </Dropdown.Item>

                              {/* DELETE Button */}
                              <Dropdown.Item
                                className="d-flex align-items-center gap-2 text-danger"
                                disabled={loading.users}
                                onClick={() => {
                                  if (!userGrant?.isDelete) {
                                    toast.warning("You are not authorized for this action!");
                                    return;
                                  }
                                  handleDeleteClick(row.id, "user");
                                }}
                              >
                                <i className="bi bi-trash" />
                                <span>Delete</span>
                              </Dropdown.Item>



                              {/* SEND MAIL */}
                              {row?.status === "unverified" && (<>

                                <Dropdown.Item
                                  className="d-flex align-items-center gap-2"
                                  onClick={() => {

                                    handleSendMail(row?.id);
                                  }}
                                >
                                  <i className="bi bi-envelope" />
                                  <span>Send Mail</span>
                                </Dropdown.Item>
                              </>)}
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      </>
                    )}
                  </>
                )}

                startidx={(userOffset * limit) + 1}
              />
              {RenderPagination(userPageButtons, userOffset, setUserOffset, limit)}
            </>
          ) : (
            <AuthorizationCard />
          )}
        </>
      )}

      {/* TEAMS conditional render */}
      {activeTab === "teams" && (
        <>
          {userGrant?.isView ? (
            <>
              <TableComponent
                data={teams}
                loading={loading?.users || loading?.teams || false}
                columns={["team_name", "description", "status", "created_at"]}
                columnLabels={{
                  team_name: "Team Name",
                  created_at: "Created",
                }}
                customRenderers={{
                  status: (row) => renderStatusBadge(row.status),
                }}
                actions={(row) => (
                  <>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="p-1 me-2"
                      onClick={() => {
                        if (!userGrant?.isEdit) {
                          toast.warning("You are not authorized for this action!");
                          return;
                        }
                        row.id && handleEditTeam(row.id);
                      }}
                      style={{ width: "30px", height: "30px" }}
                      disabled={loading.teamForm}
                      title="Edit"
                    >
                      {loading.teamForm && editTeamId === row.id ? (
                        <div className="spinner-border spinner-border-sm" />
                      ) : (
                        <i className="bi bi-pencil"></i>
                      )}
                    </Button>

                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="p-1"
                      onClick={() => {
                        if (!userGrant?.isDelete) {
                          toast.warning("You are not authorized for this action!");
                          return;
                        }
                        handleDeleteClick(row.id, "team");
                      }}
                      style={{ width: "30px", height: "30px" }}
                      disabled={loading.teams}
                      title="Delete"
                    >
                      <i className="bi bi-trash"></i>
                    </Button>
                  </>
                )}
                isLoading={isDeleting}
                startidx={(teamOffset * limit) + 1}
              />
              {RenderPagination(teamPageButtons, teamOffset, setTeamOffset, limit)}
            </>
          ) : (
            <AuthorizationCard />
          )}
        </>
      )}

      {/* User form modal */}
      <UserForm
        show={showUserForm}
        setShowTeamForm={setShowTeamForm}
        setShowUserForm={setShowUserForm}
        setAddTeam={setAddTeam}
        setAlreadySelectedTeam={setAlreadySelectedTeam}
        onHide={() => {
          setShowUserForm(false);
          setEditUserId(null);
          setEditUserData(null);
          setAddTeam(false);
        }}
        onSubmitForm={handleUserSubmit}
        editUserId={editUserId}
        initialData={editUserData}
        roles={roles}
        teams={allTeams}
        alreadySelectedTeam={alreadySelectedTeam}
        loading={loading.formSubmit || loading.userForm}
      />

      {/* Team form modal */}
      <TeamForm
        show={showTeamForm}
        setShowUserForm={setShowUserForm}
        addTeam={addTeam}
        setAddTeam={setAddTeam}
        setAlreadySelectedTeam={setAlreadySelectedTeam}
        onHide={() => {
          setShowTeamForm(false);
          setEditTeamId(null);
          setEditTeamData(null);
          setAddTeam(false);
        }}
        onSubmitForm={handleTeamSubmit}
        editTeamId={editTeamId}
        initialData={editTeamData}
        loading={loading.formSubmit || loading.teamForm}
      />

      {deleteUserLoading && (
        <div className="d-flex justify-content-center align-items-center"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(255,255,255,0.6)",
            zIndex: 2000
          }}>
          <div className="spinner-border" />
        </div>
      )}

      {deleteType === "user" && (
        <DeleteUserModal
          show={showDeleteModal}
          onClose={handleDeleteClose}
          onConfirm={handleDeleteConfirm}
          isLoading={isDeleting}
          user={deleteUserData}
        />
      )}

      {deleteType === "team" && (
        <DeleteModal
          show={showDeleteModal}
          onClose={handleDeleteClose}
          onConfirm={handleDeleteConfirm}
          isLoading={isDeleting}
        />
      )}
    </div>
  );
};